import { useState } from 'react';
import { logger } from '@fiverr-private/obs';
import { raiseToast } from '@fiverr-private/toast';
import { translate } from '@fiverr-private/i18n-react';
import { SentTemplateData, getSignedDocumentPreviewUrl, resendDocumentRequest, removeDocumentRequest } from '../../api';
import { useTemplatesData } from '../../hooks';
import { openNewTab } from '../../utils/navigation';
import { reportEventsApi } from '../../biEvents';

export interface UseDocumentActionsOptions {
    document: SentTemplateData;
}

const TRANSLATION_PREFIX = 'legal_documents_package.seller_templates.actions';
const ERROR_TRANSLATION_PREFIX = `${TRANSLATION_PREFIX}.errors`;
const SUCCESS_TRANSLATION_PREFIX = `${TRANSLATION_PREFIX}.success`;

export const useDocumentActions = ({ document }: UseDocumentActionsOptions) => {
    const [isLoadingPreviewUrl, setIsLoadingPreviewUrl] = useState(false);
    const { invalidateSellerDataQueries } = useTemplatesData({ sellerId: document.sellerId });

    const openPreviewUrl = async () => {
        try {
            reportEventsApi.downloadDocument();
            setIsLoadingPreviewUrl(true);
            const url = await getSignedDocumentPreviewUrl(document._id);
            openNewTab(url);
        } catch (error) {
            logger.error('Failed to get document preview url', error as Error);
            raiseToast({ location: 'top', type: 'error', message: translate(`${ERROR_TRANSLATION_PREFIX}.view`) });
        } finally {
            setIsLoadingPreviewUrl(false);
        }
    };

    const resendDocument = async () => {
        try {
            reportEventsApi.resendDocument();
            await resendDocumentRequest(document._id);
            invalidateSellerDataQueries();
            raiseToast({
                location: 'top',
                type: 'success',
                message: translate(`${SUCCESS_TRANSLATION_PREFIX}.resend`),
            });
        } catch (error) {
            logger.error(`Failed to resend document ${document._id}`, error as Error);
            raiseToast({ location: 'top', type: 'error', message: translate(`${ERROR_TRANSLATION_PREFIX}.resend`) });
        }
    };

    const removeDocument = async () => {
        try {
            reportEventsApi.removeDocument();
            await removeDocumentRequest(document._id);
            invalidateSellerDataQueries();
            raiseToast({
                location: 'top',
                type: 'success',
                message: translate(`${SUCCESS_TRANSLATION_PREFIX}.remove`),
            });
        } catch (error) {
            logger.error(`Failed to remove document ${document._id}`, error as Error);
            raiseToast({ location: 'top', type: 'error', message: translate(`${ERROR_TRANSLATION_PREFIX}.remove`) });
        }
    };

    return {
        openPreviewUrl,
        isLoadingPreviewUrl,
        resendDocument,
        removeDocument,
    };
};
